import DOMPurify from 'dompurify'

import { UnavailableState } from '@/shared/ui/UnavailableState'

import type { LegalDocument } from '../types'

import { LegalSection } from './LegalSection'

interface LegalDocumentContentProps {
  /** Título do documento — exibido como h1 no topo da seção. */
  title: string
  /** Documento legal completo (com body, version, effective_at). */
  document: LegalDocument | undefined
}

/**
 * Renderiza o conteúdo de um documento legal (HTML sanitizado) dentro de uma
 * seção branca full-width, com rodapé discreto mostrando versão e data de
 * vigência para transparência LGPD.
 */
export function LegalDocumentContent({ title, document }: LegalDocumentContentProps) {
  if (!document?.body) {
    return (
      <UnavailableState
        title="Conteúdo ainda não publicado"
        description="Este documento ainda não foi publicado. Volte em breve."
        className="min-h-[40vh]"
      />
    )
  }

  // body é HTML gerado pelo editor Quill do admin — mesmo padrão de
  // Document#text_content / Post#body (ver help-center/PostModal.tsx,
  // library/DocumentModal/index.tsx), não markdown. Sanitizado antes de
  // renderizar: conteúdo legal, mesmo autorado só por admin, não deve
  // confiar cegamente em dangerouslySetInnerHTML (defesa contra conta
  // admin comprometida ou link javascript: colado por engano).
  const sanitizedBody = DOMPurify.sanitize(document.body, {
    ALLOWED_TAGS: [
      'h2', 'h3', 'p', 'strong', 'em', 'u', 'ul', 'ol', 'li',
      'a', 'blockquote', 'br', 'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  })

  const effectiveDate = formatDate(document.effective_at)

  return (
    <LegalSection>
      <h1>{title}</h1>

      <div dangerouslySetInnerHTML={{ __html: sanitizedBody }} />

      <footer className="mt-12 border-t border-slate-200 pt-4 not-prose">
        <p className="text-xs text-slate-400">
          Versão {document.version} · Em vigor desde {effectiveDate}
        </p>
      </footer>
    </LegalSection>
  )
}

/** Formata ISO date string para formato brasileiro legível (ex: "21 de julho de 2026"). */
function formatDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  } catch {
    return isoDate
  }
}
